import { memo, useMemo } from "react"

import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { CostUsageTrendPoint } from "../types"

import { EmptyState } from "@/components/ui/empty-state"

import { getEmptyStates } from "./admin-dashboard-empty.utils"
import { LoadingSkeleton } from "./admin-dashboard-loading"

const CHART_HEIGHT = {
  MEDIUM: 260,
} as const

type CostUsageTrendChartProps = {
  currency: "SEK" | "USD" | "EUR"
  data: CostUsageTrendPoint[] | null | undefined
  isLoading?: boolean
}

const formatNumber = (value: number) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`
  return value.toLocaleString()
}

export const CostUsageTrendChart = memo(function CostUsageTrendChart({
  currency,
  data,
  isLoading,
}: CostUsageTrendChartProps) {
  const { t } = useTranslation()
  const EMPTY_STATES = getEmptyStates(t)

  const currencyFormatter = useMemo(() => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    })
  }, [currency])

  if (isLoading) {
    return <LoadingSkeleton height={CHART_HEIGHT.MEDIUM} variant="chart-medium" />
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <EmptyState
          description={t("adminDashboard.costsByUser.trend.emptyDescription")}
          icon={EMPTY_STATES.tokenUsage.icon}
          title={t("adminDashboard.costsByUser.trend.emptyTitle")}
        />
      </div>
    )
  }

  const costLabel = t("adminDashboard.costsByUser.trend.costLabel")
  const usageLabel = t("adminDashboard.costsByUser.trend.usageLabel")

  // Calculate interval for x-axis labels to avoid overcrowding
  const xAxisInterval = data.length > 15 ? Math.floor(data.length / 6) : 0

  return (
    <ResponsiveContainer
      aria-label="Cost and usage trend line chart over time"
      height={CHART_HEIGHT.MEDIUM}
      width="100%"
    >
      <LineChart data={data}>
        <CartesianGrid className="stroke-muted" strokeDasharray="3 3" />
        <XAxis
          angle={-45}
          className="text-muted-foreground"
          dataKey="label"
          height={70}
          interval={xAxisInterval}
          minTickGap={20}
          textAnchor="end"
          tick={{ fontSize: 11 }}
        />
        <YAxis
          allowDecimals={false}
          className="text-muted-foreground"
          domain={[0, "auto"]}
          tick={{ fontSize: 12 }}
          tickFormatter={(value) => currencyFormatter.format(Number(value))}
          yAxisId="cost"
        />
        <YAxis
          allowDecimals={false}
          className="text-muted-foreground"
          domain={[0, "auto"]}
          orientation="right"
          tick={{ fontSize: 12 }}
          tickFormatter={(value) => formatNumber(Number(value))}
          yAxisId="usage"
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "hsl(var(--popover))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "0.5rem",
            color: "hsl(var(--popover-foreground))",
          }}
          formatter={(value, name) => {
            if (name === costLabel) {
              return currencyFormatter.format(Number(value))
            }
            return formatNumber(Number(value))
          }}
          itemStyle={{
            color: "hsl(var(--popover-foreground))",
          }}
          labelStyle={{
            color: "hsl(var(--popover-foreground))",
          }}
        />
        <Legend />
        <Line
          dataKey="cost"
          dot={false}
          name={costLabel}
          stroke="hsl(var(--chart-5))"
          strokeWidth={2}
          type="monotone"
          yAxisId="cost"
        />
        <Line
          dataKey="tokensTotal"
          dot={false}
          name={usageLabel}
          stroke="hsl(var(--chart-3))"
          strokeWidth={2}
          type="monotone"
          yAxisId="usage"
        />
      </LineChart>
    </ResponsiveContainer>
  )
})
